import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import DisclaimerBody from "@/components/DisclaimerBody";

/**
 * Site-wide copyright footer + on-demand Terms link.
 *
 * Feb 2026 consolidation: the previously separate "Pastoral &
 * Medical Disclaimer" link and the "Universal Copyright, Trademark,
 * and Use Notice" modal (opened from the copyright line) are now
 * collapsed into ONE document — the full End-User License Agreement,
 * Terms of Service, Privacy Notice, and Use Terms. The copyright
 * stamp itself is no longer clickable; instead a single "Terms"
 * link in the footer opens the consolidated EULA in a read-only
 * modal. Renders below every route via App.js, hidden on /chat.
 */
export default function SiteFooter() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const previouslyFocused = useRef(null);
  const closeBtnRef = useRef(null);

  // Lock body scroll + focus the Close button while the modal is open.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused.current?.focus) {
        try { previouslyFocused.current.focus(); } catch { /* noop */ }
      }
    };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (pathname.startsWith("/chat")) return null;

  return (
    <>
      <footer
        className="rm-site-footer"
        role="contentinfo"
        data-testid="site-footer"
      >
        <p
          className="rm-site-footer-text"
          data-testid="site-footer-copyright"
        >
          © 2026 The New Jerusalem Codex Press. THE NEW JERUSALEM
          CODEX™ and THE RESTORATION MATRIX™ are claimed trademarks
          of the owner/licensor. All rights reserved.
        </p>
        <p className="rm-site-footer-links">
          <a
            href="https://newjerusalemcodex.org"
            target="_blank"
            rel="noopener noreferrer"
            className="rm-site-footer-button"
            data-testid="site-footer-parent-site-link"
          >
            Visit NewJerusalemCodex.org
          </a>
          <button
            type="button"
            className="rm-site-footer-link"
            onClick={() => setOpen(true)}
            data-testid="site-footer-terms-link"
          >
            Terms of Service &amp; EULA
          </button>
        </p>
      </footer>

      {open && (
        <div
          className="rm-disclaimer-backdrop"
          role="presentation"
          onClick={() => setOpen(false)}
          data-testid="site-footer-modal-backdrop"
        >
          <div
            className="rm-disclaimer-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rm-footer-terms-title"
            onClick={(e) => e.stopPropagation()}
            data-testid="site-footer-terms-modal"
          >
            <h2 id="rm-footer-terms-title" className="rm-disclaimer-title">
              End-User License Agreement &amp; Terms of Service
            </h2>
            <DisclaimerBody showConsentFooter={false} />
            <div className="rm-disclaimer-actions">
              <button
                ref={closeBtnRef}
                type="button"
                className="rm-btn-solid rm-disclaimer-continue"
                onClick={() => setOpen(false)}
                data-testid="site-footer-terms-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
